import React, { useState, useEffect } from 'react';
import { Container} from 'react-bootstrap';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
// import styled from 'styled-components';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
const AboutAfar = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timelineIndex, setTimelineIndex] = useState(0);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const timelineData = [
    { year: '1000 BC', event: 'Early Afar settlements' },
    { year: '1200s', event: 'Islamic influence begins' },
    { year: '1800s', event: 'European exploration era' },
    { year: '1991', event: 'Formation of modern Afar region' },
    { year: 'Present', event: 'Emerging tourist destination' }
  ];

  const tabData = {
    overview: {
      title: 'Overview',
      content: 'The Afar Region, located in northeastern Ethiopia, is known for its extreme landscapes, volcanic activity, and unique cultural heritage.'
    },
    culture: {
      title: 'Culture',
      content: 'The Afar people maintain rich traditions, with distinctive music, dance, and social structures passed down through generations.'
    },
    attractions: {
      title: 'Attractions',
      content: 'Home to the Danakil Depression, active volcanoes, salt flats, and colorful hydrothermal features.'
    },
    climate: {
      title: 'Climate',
      content: 'One of the hottest places on Earth, with temperatures regularly exceeding 45°C (113°F) in the Danakil Desert.'
    }
  };

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <StyledAfar ref={ref}>
      <HeroSection>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="hero-content"
        >
          <h1>Welcome to Afar Region</h1>
          <p>Discover the Land of Fire and Salt</p>
        </motion.div>
      </HeroSection>

      <Container>
        <TabSection>
          <div className="tabs">
            {Object.keys(tabData).map(tab => (
              <TabButton
                key={tab}
                active={activeTab === tab}
                onClick={() => setActiveTab(tab)}
              >
                {tabData[tab].title}
              </TabButton>
            ))}
          </div>
          <AnimatePresence mode='wait'>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="tab-content"
            >
              {tabData[activeTab].content}
            </motion.div>
          </AnimatePresence>
        </TabSection>

        <TimelineSection>
          <h2>History of Afar</h2>
          <div className="timeline">
            {timelineData.map((item, index) => (
              <motion.div
                key={index}
                className={`timeline-item ${timelineIndex === index ? 'active' : ''}`}
                initial={{ opacity: 0, x: -50 }}
                animate={controls}
                variants={{
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: { delay: index * 0.2 }
                  }
                }}
                onClick={() => setTimelineIndex(index)}
              >
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <h3>{item.year}</h3>
                  <p>{item.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </TimelineSection>

        <FeatureGrid>
          {[
            { icon: '🌋', title: 'Volcanic Wonders', desc: 'Active volcanoes and lava lakes' },
            { icon: '🏜️', title: 'Desert Landscapes', desc: 'Vast salt flats and mineral formations' },
            { icon: '👥', title: 'Indigenous Culture', desc: 'Traditional Afar communities' },
            { icon: '🦒', title: 'Wildlife', desc: 'Unique desert adapted species' }
          ].map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              whileHover={{ scale: 1.05 }}
              initial={{ opacity: 0, y: 50 }}
              animate={controls}
              variants={{
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { delay: index * 0.1 }
                }
              }}
            >
              <span className="feature-icon">{feature.icon}</span>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </motion.div>
          ))}
        </FeatureGrid>
      </Container>
    </StyledAfar>
  );
};

// Styled components would follow here...


const HeroSection = styled.section`
  height: 80vh;
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
              url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"><path fill="%23273036" fill-opacity="1" d="M0,160L48,170.7C96,181,192,203,288,192C384,181,480,139,576,138.7C672,139,768,181,864,181.3C960,181,1056,139,1152,122.7C1248,107,1344,117,1392,122.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path></svg>');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;

  .hero-content {
    z-index: 2;
    h1 {
      font-size: 4rem;
      margin-bottom: 1rem;
      font-weight: 700;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
    }
    p {
      font-size: 1.5rem;
      font-weight: 300;
      text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, #f8f9fa, transparent);
  }
`;

const TabSection = styled.section`
  margin: 4rem 0;
  padding: 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);

  .tabs {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 2rem;
    flex-wrap: wrap;
  }

  .tab-content {
    padding: 2rem;
    min-height: 200px;
    text-align: center;
    font-size: 1.1rem;
    line-height: 1.8;
    color: #4a5568;
  }

  @media (max-width: 768px) {
    .tabs {
      flex-direction: column;
    }
  }
`;

const TabButton = styled.button`
  padding: 1rem 2rem;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.active ? 'linear-gradient(45deg, #2d7ba8, #34495e)' : '#f1f5f9'};
  color: ${props => props.active ? 'white' : '#64748b'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }
`;

const TimelineSection = styled.section`
  margin: 6rem 0;
  padding: 2rem;

  h2 {
    text-align: center;
    margin-bottom: 3rem;
    color: #1e293b;
    font-weight: 700;
  }

  .timeline {
    position: relative;
    max-width: 800px;
    margin: 0 auto;

    &::before {
      content: '';
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      width: 2px;
      height: 100%;
      background: #cbd5e1;
    }
  }

  .timeline-item {
    position: relative;
    padding: 2rem;
    width: 50%;
    cursor: pointer;

    &:nth-child(odd) {
      left: 0;
      padding-right: 3rem;
      .timeline-content {
        text-align: right;
      }
    }

    &:nth-child(even) {
      left: 50%;
      padding-left: 3rem;
    }

    &.active .timeline-dot {
      background: #2d7ba8;
      transform: scale(1.5);
    }
  }

  .timeline-dot {
    position: absolute;
    right: -10px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #94a3b8;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.3);
    }
  }

  .timeline-content {
    background: white;
    padding: 1.5rem;
    border-radius: 15px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    transition: all 0.3s ease;

    h3 {
      color: #2d7ba8;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }

    p {
      color: #64748b;
      margin: 0;
    }

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0,0,0,0.1);
    }
  }

  @media (max-width: 768px) {
    .timeline::before {
      left: 0;
    }

    .timeline-item {
      width: 100%;
      padding-left: 2rem !important;
      left: 0 !important;

      .timeline-content {
        text-align: left !important;
      }
    }

    .timeline-dot {
      left: -10px !important;
    }
  }
`;

const FeatureGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
  padding: 2rem;

  .feature-card {
    background: white;
    padding: 2rem;
    border-radius: 20px;
    text-align: center;
    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
    transition: all 0.3s ease;

    .feature-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      display: block;
    }

    h3 {
      color: #1e293b;
      font-weight: 600;
      margin-bottom: 1rem;
    }

    p {
      color: #64748b;
      margin: 0;
    }

    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 20px 40px rgba(0,0,0,0.1);
    }
  }
`;

// Add these animations at the end
// const fadeInUp = keyframes`
//   from {
//     opacity: 0;
//     transform: translateY(20px);
//   }
//   to {
//     opacity: 1;
//     transform: translateY(0);
//   }
// `;

// const GlobalStyle = createGlobalStyle`
//   @keyframes float {
//     0%, 100% { transform: translateY(0); }
//     50% { transform: translateY(-10px); }
//   }

//   .animate-float {
//     animation: float 3s ease-in-out infinite;
//   }
// `;
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const StyledAfar = styled.div`
  background: #f8f9fa;
  overflow: hidden;

  .animate-float {
    animation: ${floatAnimation} 3s ease-in-out infinite;
  }

  .fade-in-up {
    animation: ${fadeInUp} 0.6s ease-out forwards;
  }
`;
export default AboutAfar;